import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import AdminLayout from "@/components/admin/AdminLayout";
import { BrutalCard } from "@/components/ui/BrutalCard";
import { BrutalButton } from "@/components/ui/BrutalButton";
import KPICard from "@/components/admin/KPICard";
import { BadgeCard } from "@/components/admin/BadgeCard";
import { BadgeFormModal } from "@/components/admin/BadgeFormModal";
import { ArrowLeft, Award, Plus, Users, CheckCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";

interface Badge {
  id: string;
  name_it: string;
  name_en: string | null;
  name_es: string | null;
  description_it: string | null;
  description_en: string | null;
  description_es: string | null;
  image_url: string | null;
  badge_type: string;
  is_active: boolean;
  created_at: string;
  user_count?: number;
}

export default function AdminBadges() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [badges, setBadges] = useState<Badge[]>([]);
  const [loading, setLoading] = useState(true);
  const [showFormModal, setShowFormModal] = useState(false);
  const [selectedBadge, setSelectedBadge] = useState<Badge | null>(null);

  useEffect(() => {
    loadBadges();
  }, []);

  const loadBadges = async () => {
    try {
      // Fetch badges
      const { data: badgesData, error: badgesError } = await supabase
        .from("badges")
        .select("*")
        .order("created_at", { ascending: false });

      if (badgesError) throw badgesError;

      // Fetch user counts for each badge
      const { data: userBadgesData, error: userBadgesError } = await supabase
        .from("user_badges")
        .select("badge_id");

      if (userBadgesError) throw userBadgesError;

      // Count users per badge
      const badgeCounts = (userBadgesData || []).reduce((acc, ub) => {
        acc[ub.badge_id] = (acc[ub.badge_id] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);

      // Merge counts with badges
      const badgesWithCounts = (badgesData || []).map(badge => ({
        ...badge,
        user_count: badgeCounts[badge.id] || 0
      }));

      setBadges(badgesWithCounts);
    } catch (error) {
      console.error("Error loading badges:", error);
      toast({ title: t("admin.badges.loadError"), variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (badge: Badge) => {
    setSelectedBadge(badge);
    setShowFormModal(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm(t("admin.badges.confirmDelete"))) return;

    try {
      const { error } = await supabase
        .from("badges")
        .delete()
        .eq("id", id);

      if (error) throw error;

      toast({ title: t("admin.badges.deleteSuccess"), variant: "success" });
      loadBadges();
    } catch (error) {
      console.error("Error deleting badge:", error);
      toast({ title: t("admin.badges.deleteError"), variant: "error" });
    }
  };

  const handleFormClose = () => {
    setShowFormModal(false);
    setSelectedBadge(null);
  };

  const handleFormSuccess = () => {
    handleFormClose();
    loadBadges();
  };

  const getBadgeName = (badge: Badge) => {
    const lang = i18n.language;
    if (lang === 'es' && badge.name_es) return badge.name_es;
    if (lang === 'en' && badge.name_en) return badge.name_en;
    return badge.name_it;
  };

  // KPIs
  const totalBadges = badges.length;
  const activeBadges = badges.filter(b => b.is_active).length;
  const totalUsersWithBadges = badges.reduce((acc, b) => acc + (b.user_count || 0), 0);

  if (loading) {
    return (
      <AdminLayout>
        <div className="p-8">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-surface rounded w-1/4"></div>
            <div className="h-96 bg-surface rounded"></div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-8">
        <Breadcrumb className="mb-6">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink href="/admin/dashboard">Dashboard</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>{t("admin.badges.breadcrumb")}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigate("/admin/dashboard")}
            className="p-2 hover:bg-accent transition-colors rounded-lg"
            aria-label={t("admin.badges.backButton")}
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="font-heading text-4xl font-bold flex items-center gap-3">
              <Award className="w-10 h-10 text-primary" />
              {t("admin.badges.title")}
            </h1>
            <p className="text-muted-foreground mt-1">{t("admin.badges.subtitle")}</p>
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <KPICard
            title={t("admin.badges.kpi.total")}
            value={totalBadges}
          />
          <KPICard
            title={t("admin.badges.kpi.active")}
            value={activeBadges}
          />
          <KPICard
            title={t("admin.badges.kpi.usersWithBadges")}
            value={totalUsersWithBadges}
          />
        </div>

        {/* Action button */}
        <div className="mb-6">
          <BrutalButton
            onClick={() => setShowFormModal(true)}
            variant="primary"
            className="flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            {t("admin.badges.createNew")}
          </BrutalButton>
        </div>

        {/* Badges grid */}
        {badges.length === 0 ? (
          <BrutalCard className="p-12 text-center">
            <Award className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
            <h3 className="font-heading text-xl font-bold mb-2">
              {t("admin.badges.noBadges")}
            </h3>
            <p className="text-muted-foreground mb-4">
              {t("admin.badges.noBadgesDescription")}
            </p>
            <BrutalButton
              onClick={() => setShowFormModal(true)}
              variant="primary"
            >
              <Plus className="w-5 h-5 mr-2" />
              {t("admin.badges.createFirst")}
            </BrutalButton>
          </BrutalCard>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {badges.map((badge) => (
              <BadgeCard
                key={badge.id}
                badge={badge}
                badgeName={getBadgeName(badge)}
                onEdit={() => handleEdit(badge)}
                onDelete={() => handleDelete(badge.id)}
              />
            ))}
          </div>
        )}

        {/* Form Modal */}
        <BadgeFormModal
          open={showFormModal}
          onClose={handleFormClose}
          onSuccess={handleFormSuccess}
          badge={selectedBadge}
        />
      </div>
    </AdminLayout>
  );
}